import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { EnvironmentsService, IOcbAdministrator, IOcbEnvironment, IOcbEnvironmentAccess, UserService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const routerTransition = trigger('routerTransition', [
    transition('* => *', [
        query(':enter .global, :leave .global, :enter .dx-field-title, :leave .dx-field-title, :enter .main, :leave .main',
            style({ opacity: 0 }), { optional: true }),
        group([
            query(':enter .dx-field-title, :enter .main', [
                style({ transform: 'translate(0, 30px)' }),
                animate('700ms ease', style({ transform: 'translate(0, 0)', opacity: 1 })),
            ], { optional: true }),
        ]),
    ]),
]);

@Component({
    selector: 'app-main-layout',
    templateUrl: './main-layout.component.html',
    styleUrls: ['./main-layout.component.scss'],
    animations: [routerTransition],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
    public userService: UserService;
    public environmentsService: EnvironmentsService;
    public translate: TranslateService;
    public isCollapsed: boolean;
    public environmentId: string;
    public environmentName: string;
    public user: IOcbAdministrator;
    public allEnvironments: IOcbEnvironment[];
    public activeEnvironments: IOcbEnvironmentAccess[];

    public envPopupVisible: boolean = false;

    private router: Router;
    private activatedRoute: ActivatedRoute;
    private userSubscription: Subscription;

    constructor(userService: UserService,
        environmentsService: EnvironmentsService,
        translate: TranslateService,
        router: Router,
        activatedRoute: ActivatedRoute) {
        this.userService = userService;
        this.environmentsService = environmentsService;
        this.translate = translate;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.isCollapsed = false;
    }

    public ngOnInit(): void {
        this.userSubscription =
            this.userService.user
                .subscribe(user => {
                    this.user = user;
                    this.environmentsService.getEnvironments(true).subscribe(envs => {
                        this.allEnvironments = envs;
                        this.activeEnvironments = this.user.environments.filter(env => this.allEnvironments.some(e => e.id === env.environmentId && !e.isDisabled));
                    });
                    this.restoreEnvironment(user);
                    this.environmentName = user.environments.find(env => env.environmentId === this.environmentId).environmentName;
                });
    }

    public ngOnDestroy(): void {
        this.userSubscription.unsubscribe();
    }

    public selectEnvironment(environment: IOcbEnvironmentAccess) {
        if (this.environmentId !== environment.environmentId) {
            this.environmentId = environment.environmentId;
            this.environmentName = environment.environmentName;

            const currentId = this.activatedRoute.children[0].snapshot.params.id;
            if (currentId) {
                const newUrl = location.pathname.replace(currentId, environment.environmentId);
                this.router.navigateByUrl(newUrl);
            }
            else {
                this.router.navigateByUrl(location.pathname);
            }

            this.userService.currentEnvironment = environment.environmentId;
            this.envPopupVisible = false;
        }
    }

    public selectLanguage(language: string) {
        this.translate.use(language);
    }

    public toggleNavigation() {
        this.isCollapsed = !this.isCollapsed;
    }

    public logout() {
        this.userService.logout();
        this.router.navigate(['/login']);
    }

    public getState(outlet: RouterOutlet) {
        return outlet.isActivated ? outlet.activatedRoute : '';
    }

    private restoreEnvironment(user: IOcbAdministrator): void {
        // Select current environment from the url
        let envId = this.activatedRoute.children[0].snapshot.params.id;

        //// Else select from storage
        if (!envId) {
            envId = this.userService.currentEnvironment;
        }

        // Find environment by id
        let environment = user.environments.find(env => env.environmentId === envId);

        // If none is found, use the first environment in the list
        if (!environment) {
            environment = user.environments[0];
        }

        this.environmentId = environment.environmentId;
        this.userService.currentEnvironment = environment.environmentId;
    }
}
